import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PriceMetrics } from './components/PriceMetrics';
import { LineChart } from './components/LineChart';
import { VolumeChart } from './components/VolumeChart';
import { VolatilityIndicator } from './components/VolatilityIndicator';
import { HistoricalView } from './components/HistoricalView';
import { ShortSqueezeDetector } from './components/ShortSqueezeDetector';
import type { TimeRange, IndicatorType, IndicatorPeriod } from './types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 30000,
    },
  },
});

function App() {
  const [range, setRange] = useState<TimeRange>('1d');
  const [indicatorType, setIndicatorType] = useState<IndicatorType>('sma');
  const [indicatorPeriod, setIndicatorPeriod] = useState<IndicatorPeriod>(20);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg">
          <div className="container mx-auto px-4 py-6">
            <h1 className="text-3xl font-bold">S&P 500 Futures Live Dashboard</h1>
            <p className="text-blue-100 mt-1">Real-time E-mini S&P 500 (ES=F) Analytics</p>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-6">
          {/* Price Metrics - Full Width */}
          <div className="mb-6">
            <ErrorBoundary>
              <PriceMetrics />
            </ErrorBoundary>
          </div>

          {/* Short Squeeze Detector - Full Width */}
          <div className="mb-6">
            <ErrorBoundary>
              <ShortSqueezeDetector />
            </ErrorBoundary>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 gap-6 mb-6">
            {/* Controls for Charts */}
            <div className="bg-white rounded-lg shadow-lg p-4">
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time Range
                  </label>
                  <div className="flex gap-2">
                    {(['1d', '5d', '1mo', '3mo', '6mo', '1y'] as TimeRange[]).map((tr) => (
                      <button
                        key={tr}
                        onClick={() => setRange(tr)}
                        className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                          range === tr
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tr.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Indicator Type
                  </label>
                  <select
                    value={indicatorType}
                    onChange={(e) => setIndicatorType(e.target.value as IndicatorType)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="sma">SMA (Simple Moving Average)</option>
                    <option value="ema">EMA (Exponential Moving Average)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Period
                  </label>
                  <select
                    value={indicatorPeriod}
                    onChange={(e) => setIndicatorPeriod(parseInt(e.target.value) as IndicatorPeriod)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Line Chart with Indicator */}
            <ErrorBoundary>
              <LineChart
                range={range}
                indicatorType={indicatorType}
                indicatorPeriod={indicatorPeriod}
              />
            </ErrorBoundary>

            {/* Volume Chart */}
            <ErrorBoundary>
              <VolumeChart range={range} />
            </ErrorBoundary>
          </div>

          {/* Bottom Section - 2 Column Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Volatility Indicator */}
            <ErrorBoundary>
              <VolatilityIndicator range={range} />
            </ErrorBoundary>

            {/* Historical View - Spans both columns on smaller screens */}
            <div className="lg:col-span-2">
              <ErrorBoundary>
                <HistoricalView />
              </ErrorBoundary>
            </div>
          </div>

          {/* Footer Info */}
          <div className="mt-8 p-4 bg-white rounded-lg shadow text-center text-sm text-gray-600">
            <p>
              Data updates every minute via Cloudflare Workers and R2 Data Catalog.
              <br />
              Powered by Yahoo Finance API (ES=F) • Built with React + Cloudflare Workers
            </p>
          </div>
        </main>
      </div>
    </QueryClientProvider>
  );
}

export default App;
